import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { CREST_URL } from "../components/SiteLayout";
import { toast } from "sonner";
import { LogOut, Trash2, CheckCircle2, Circle, Mail, Phone, Search, DollarSign, Clock, AlertCircle } from "lucide-react";
import { AdminTopbar } from "./AdminMembersPage";

const AdminDashboardPage = () => {
  const { user, logout } = useAuth();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, unreviewed: 0, prospective: 0, contact: 0, paid: 0, payment_pending: 0, payment_clicked: 0 });
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const nav = useNavigate();

  const load = async () => {
    try {
      const [a, b] = await Promise.all([api.get("/interest"), api.get("/admin/stats")]);
      setItems(a.data);
      setStats(b.data);
    } catch (_) {
      toast.error("Failed to load submissions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleLogout = async () => {
    await logout();
    nav("/");
  };

  const toggleReviewed = async (id) => {
    try {
      const { data } = await api.patch(`/interest/${id}/toggle-reviewed`);
      setItems((arr) => arr.map((it) => (it.id === id ? data : it)));
      if (selected?.id === id) setSelected(data);
      load();
    } catch {
      toast.error("Could not update status");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this submission?")) return;
    try {
      await api.delete(`/interest/${id}`);
      setItems((arr) => arr.filter((it) => it.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Submission deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const togglePaymentVerified = async (id) => {
    try {
      const { data } = await api.patch(`/interest/${id}/toggle-payment-verified`);
      setItems((arr) => arr.map((it) => (it.id === id ? data : it)));
      if (selected?.id === id) setSelected(data);
      load();
    } catch {
      toast.error("Could not update payment status");
    }
  };

  const filtered = items.filter((it) => {
    if (filter !== "all" && it.interest_type !== filter) return false;
    if (paymentFilter !== "all") {
      if (paymentFilter === "verified" && !it.payment_verified) return false;
      if (paymentFilter === "clicked" && (it.payment_verified || !it.payment_clicked_at)) return false;
      if (paymentFilter === "pending" && (it.payment_verified || it.payment_clicked_at || it.interest_type !== "prospective")) return false;
    }
    if (!q) return true;
    const h = `${it.name} ${it.email} ${it.major} ${it.dorm} ${it.message || ""}`.toLowerCase();
    return h.includes(q.toLowerCase());
  });

  return (
    <div className="admin-shell" data-testid="admin-dashboard">
      <AdminTopbar user={user} onLogout={handleLogout} active="submissions" />

      <div className="max-w-[1400px] mx-auto px-6 py-10">
        <h1 className="font-serif-display text-4xl text-[#0A1F44]">Interest Submissions</h1>
        <p className="text-[#5C5C5C] mt-2">Review prospective member inquiries and general contact messages.</p>

        {/* Stats */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-5" data-testid="stats-grid">
          <Stat label="Total" value={stats.total} />
          <Stat label="Unreviewed" value={stats.unreviewed} />
          <Stat label="Prospective" value={stats.prospective} />
          <Stat label="Contact" value={stats.contact} />
          <Stat label="Paid" value={stats.paid} accent="green" />
          <Stat label="Clicked Pay" value={stats.payment_clicked} accent="gold" />
          <Stat label="Awaiting Pay" value={stats.payment_pending} accent="red" />
        </div>

        {/* Controls */}
        <div className="mt-10 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2">
            <span className="text-[10px] tracking-[0.22em] uppercase text-[#5C5C5C] self-center mr-2">Type:</span>
            {["all", "prospective", "contact"].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-4 py-2 text-xs tracking-[0.22em] uppercase border transition-colors ${
                  filter === f
                    ? "bg-[#0A1F44] text-white border-[#0A1F44]"
                    : "bg-white text-[#0A1F44] border-[#D8D3C4] hover:border-[#0A1F44]"
                }`}
                data-testid={`filter-${f}`}
              >
                {f}
              </button>
            ))}
            <span className="text-[10px] tracking-[0.22em] uppercase text-[#5C5C5C] self-center mx-2 ml-4">Payment:</span>
            {[
              { v: "all", l: "All" },
              { v: "verified", l: "Verified" },
              { v: "clicked", l: "Clicked" },
              { v: "pending", l: "Not Started" },
            ].map((f) => (
              <button
                key={f.v}
                onClick={() => setPaymentFilter(f.v)}
                className={`px-4 py-2 text-xs tracking-[0.22em] uppercase border transition-colors ${
                  paymentFilter === f.v
                    ? "bg-[#D4AF37] text-black border-[#D4AF37]"
                    : "bg-white text-[#0A1F44] border-[#D8D3C4] hover:border-[#D4AF37]"
                }`}
                data-testid={`payment-filter-${f.v}`}
              >
                {f.l}
              </button>
            ))}
          </div>
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C5C]" />
            <input
              placeholder="Search submissions..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 pr-3 py-2.5 w-full lg:w-80 border border-[#D8D3C4] bg-white text-sm focus:outline-none focus:border-[#D4AF37] rounded-none"
              data-testid="admin-search"
            />
          </div>
        </div>

        {/* Table */}
        <div className="mt-6 border border-[#D8D3C4] bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="submissions-table">
            <thead className="bg-[#F5F1E6] text-[#0A1F44]">
              <tr>
                <th className="text-left px-5 py-3 overline">Status</th>
                <th className="text-left px-5 py-3 overline">Name</th>
                <th className="text-left px-5 py-3 overline">Email</th>
                <th className="text-left px-5 py-3 overline">Type</th>
                <th className="text-left px-5 py-3 overline">Payment</th>
                <th className="text-left px-5 py-3 overline">Submitted</th>
                <th className="text-right px-5 py-3 overline">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-5 py-10 text-center text-[#5C5C5C]">
                    Loading...
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-5 py-10 text-center text-[#5C5C5C]" data-testid="submissions-empty">
                    No submissions match your filters.
                  </td>
                </tr>
              ) : (
                filtered.map((it) => (
                  <tr key={it.id} className="border-t border-[#D8D3C4] hover:bg-[#FAF7ED] cursor-pointer" onClick={() => setSelected(it)} data-testid={`row-${it.id}`}>
                    <td className="px-5 py-4">
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleReviewed(it.id); }}
                        className={`inline-flex items-center gap-1.5 text-xs ${it.reviewed ? "text-green-700" : "text-[#C9A227]"}`}
                        data-testid={`toggle-${it.id}`}
                      >
                        {it.reviewed ? <CheckCircle2 size={16} /> : <Circle size={16} />}
                        {it.reviewed ? "Reviewed" : "New"}
                      </button>
                    </td>
                    <td className="px-5 py-4 font-medium">{it.name}</td>
                    <td className="px-5 py-4 text-[#5C5C5C]">{it.email}</td>
                    <td className="px-5 py-4">
                      <span className={`text-[11px] tracking-[0.2em] uppercase px-2 py-1 border ${it.interest_type === "prospective" ? "border-[#0A1F44] text-[#0A1F44]" : "border-[#C9A227] text-[#C9A227]"}`}>
                        {it.interest_type}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <PaymentBadge item={it} />
                    </td>
                    <td className="px-5 py-4 text-[#5C5C5C]">
                      {new Date(it.created_at).toLocaleString()}
                    </td>
                    <td className="px-5 py-4 text-right">
                      <button
                        onClick={(e) => { e.stopPropagation(); del(it.id); }}
                        className="text-red-600 hover:text-red-800"
                        data-testid={`delete-${it.id}`}
                        aria-label="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex" data-testid="submission-detail">
          <div className="flex-1 bg-black/40" onClick={() => setSelected(null)} />
          <div className="w-full max-w-lg bg-white h-full overflow-y-auto shadow-2xl">
            <div className="p-6 bg-[#0A1F44] text-white flex items-center justify-between">
              <div>
                <div className="overline text-[#D4AF37]">{selected.interest_type}</div>
                <div className="font-serif-display text-2xl mt-1">{selected.name}</div>
              </div>
              <button onClick={() => setSelected(null)} className="text-white hover:text-[#D4AF37]" data-testid="close-detail">
                Close
              </button>
            </div>
            <div className="p-6 space-y-5 text-[#24324F]">
              <Row label="Email" value={<a href={`mailto:${selected.email}`} className="text-[#0A1F44] hover:text-[#C9A227] inline-flex items-center gap-2"><Mail size={14} />{selected.email}</a>} />
              {selected.phone && <Row label="Phone" value={<span className="inline-flex items-center gap-2"><Phone size={14} />{selected.phone}</span>} />}
              {selected.classification && <Row label="Classification" value={selected.classification} />}
              {selected.major && <Row label="Major" value={selected.major} />}
              {selected.dorm && <Row label="Dorm" value={selected.dorm} />}
              <Row label="Submitted" value={new Date(selected.created_at).toLocaleString()} />
              {selected.interest_type === "prospective" && (
                <div>
                  <div className="overline text-[#5C5C5C] mb-2">Payment Status</div>
                  <div className="bg-[#F5F1E6] border border-[#D8D3C4] p-4">
                    <PaymentBadge item={selected} large />
                    {selected.payment_clicked_at && (
                      <div className="text-xs text-[#5C5C5C] mt-2">
                        Clicked Cash App: {new Date(selected.payment_clicked_at).toLocaleString()}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {selected.message && (
                <div>
                  <div className="overline text-[#5C5C5C] mb-2">Message</div>
                  <div className="bg-[#F5F1E6] border border-[#D8D3C4] p-4 whitespace-pre-wrap leading-relaxed">
                    {selected.message}
                  </div>
                </div>
              )}
              {selected.interest_type === "prospective" && (
                <EssaysSection submission={selected} />
              )}
              <div className="flex flex-wrap gap-3 pt-4">
                <button
                  onClick={() => toggleReviewed(selected.id)}
                  className="btn-outline-navy"
                  data-testid="detail-toggle-reviewed"
                >
                  {selected.reviewed ? "Mark New" : "Mark Reviewed"}
                </button>
                {selected.interest_type === "prospective" && (
                  <button
                    onClick={() => togglePaymentVerified(selected.id)}
                    className={`px-6 py-3 text-xs tracking-[0.22em] uppercase border transition-colors ${
                      selected.payment_verified
                        ? "bg-white border-green-700 text-green-700 hover:bg-green-50"
                        : "bg-[#D4AF37] border-[#D4AF37] text-black hover:bg-[#C9A227]"
                    }`}
                    data-testid="detail-toggle-payment"
                  >
                    {selected.payment_verified ? "Mark Unpaid" : "Mark Paid"}
                  </button>
                )}
                <button onClick={() => del(selected.id)} className="btn-primary-navy bg-red-700 border-red-700 hover:bg-red-900 hover:border-red-900 hover:text-white" data-testid="detail-delete">
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value, accent }) => {
  const ruleColor = accent === "green" ? "bg-green-700" : accent === "red" ? "bg-red-600" : "bg-[#D4AF37]";
  return (
    <div className="bg-white border border-[#D8D3C4] p-5">
      <div className="overline text-[#5C5C5C]">{label}</div>
      <div className="font-serif-display text-4xl text-[#0A1F44] mt-2">{value}</div>
      <div className={`h-[2px] w-8 mt-3 ${ruleColor}`} />
    </div>
  );
};

const PaymentBadge = ({ item, large = false }) => {
  if (item.interest_type !== "prospective") {
    return <span className="text-xs text-[#5C5C5C]">&mdash;</span>;
  }
  const cls = large
    ? "inline-flex items-center gap-2 px-3 py-2 border text-sm"
    : "inline-flex items-center gap-1.5 px-2 py-1 border text-[11px] tracking-[0.18em] uppercase";
  if (item.payment_verified) {
    return (
      <span className={`${cls} border-green-700 text-green-700 bg-green-50`} data-testid="payment-verified">
        <DollarSign size={large ? 16 : 13} /> Paid
      </span>
    );
  }
  if (item.payment_clicked_at) {
    return (
      <span className={`${cls} border-[#D4AF37] text-[#8A6E14] bg-[#FBF6E4]`} data-testid="payment-clicked">
        <Clock size={large ? 16 : 13} /> Clicked Pay
      </span>
    );
  }
  return (
    <span className={`${cls} border-red-400 text-red-700 bg-red-50`} data-testid="payment-pending">
      <AlertCircle size={large ? 16 : 13} /> Not Started
    </span>
  );
};

const Row = ({ label, value }) => (
  <div>
    <div className="overline text-[#5C5C5C] mb-1">{label}</div>
    <div>{value}</div>
  </div>
);

const ESSAY_FIELDS = [
  { key: "honors_awards", label: "High School honors, awards & scholarships" },
  { key: "future_plans", label: "Plans for the future" },
  { key: "about_yourself", label: "About themselves" },
  { key: "howard_experience", label: "What they want from Howard" },
  { key: "proposed_event", label: "Event they would run under GODSC" },
  { key: "tangible_skills", label: "Tangible skills they bring" },
  { key: "why_godsc", label: "Why they want to be a member" },
];

const EssaysSection = ({ submission }) => {
  const hasAny = ESSAY_FIELDS.some((f) => submission[f.key]);
  if (!hasAny) return null;
  return (
    <div data-testid="detail-essays">
      <div className="overline text-[#5C5C5C] mb-2">Application Essays</div>
      <div className="space-y-4">
        {ESSAY_FIELDS.map((f) =>
          submission[f.key] ? (
            <div key={f.key} className="bg-[#F5F1E6] border border-[#D8D3C4] p-4">
              <div className="text-[11px] tracking-[0.2em] uppercase text-[#C9A227] mb-1.5">
                {f.label}
              </div>
              <div className="whitespace-pre-wrap leading-relaxed text-sm text-[#24324F]">
                {submission[f.key]}
              </div>
            </div>
          ) : null
        )}
      </div>
    </div>
  );
};

export default AdminDashboardPage;
